/**
 * 半角カタカナ → 全角カタカナ 変換マップ
 * 濁点・半濁点は次の文字と結合する
 */
const HALF_TO_FULL_KATAKANA: Record<string, string> = {
  ｦ: 'ヲ',
  ｧ: 'ァ',
  ｨ: 'ィ',
  ｩ: 'ゥ',
  ｪ: 'ェ',
  ｫ: 'ォ',
  ｬ: 'ャ',
  ｭ: 'ュ',
  ｮ: 'ョ',
  ｯ: 'ッ',
  ｰ: 'ー',
  ｱ: 'ア',
  ｲ: 'イ',
  ｳ: 'ウ',
  ｴ: 'エ',
  ｵ: 'オ',
  ｶ: 'カ',
  ｷ: 'キ',
  ｸ: 'ク',
  ｹ: 'ケ',
  ｺ: 'コ',
  ｻ: 'サ',
  ｼ: 'シ',
  ｽ: 'ス',
  ｾ: 'セ',
  ｿ: 'ソ',
  ﾀ: 'タ',
  ﾁ: 'チ',
  ﾂ: 'ツ',
  ﾃ: 'テ',
  ﾄ: 'ト',
  ﾅ: 'ナ',
  ﾆ: 'ニ',
  ﾇ: 'ヌ',
  ﾈ: 'ネ',
  ﾉ: 'ノ',
  ﾊ: 'ハ',
  ﾋ: 'ヒ',
  ﾌ: 'フ',
  ﾍ: 'ヘ',
  ﾎ: 'ホ',
  ﾏ: 'マ',
  ﾐ: 'ミ',
  ﾑ: 'ム',
  ﾒ: 'メ',
  ﾓ: 'モ',
  ﾔ: 'ヤ',
  ﾕ: 'ユ',
  ﾖ: 'ヨ',
  ﾗ: 'ラ',
  ﾘ: 'リ',
  ﾙ: 'ル',
  ﾚ: 'レ',
  ﾛ: 'ロ',
  ﾜ: 'ワ',
  ﾝ: 'ン',
};

/** 濁点結合可能な文字 */
const DAKUTEN_MAP: Record<string, string> = {
  カ: 'ガ',
  キ: 'ギ',
  ク: 'グ',
  ケ: 'ゲ',
  コ: 'ゴ',
  サ: 'ザ',
  シ: 'ジ',
  ス: 'ズ',
  セ: 'ゼ',
  ソ: 'ゾ',
  タ: 'ダ',
  チ: 'ヂ',
  ツ: 'ヅ',
  テ: 'デ',
  ト: 'ド',
  ハ: 'バ',
  ヒ: 'ビ',
  フ: 'ブ',
  ヘ: 'ベ',
  ホ: 'ボ',
  ウ: 'ヴ',
};

/** 半濁点結合可能な文字 */
const HANDAKUTEN_MAP: Record<string, string> = {
  ハ: 'パ',
  ヒ: 'ピ',
  フ: 'プ',
  ヘ: 'ペ',
  ホ: 'ポ',
};

/**
 * 半角カタカナを全角カタカナに変換
 */
export function halfToFullKatakana(str: string): string {
  let result = '';
  let i = 0;

  while (i < str.length) {
    const char = str[i];
    const nextChar = str[i + 1];

    // 半角カタカナの場合
    if (HALF_TO_FULL_KATAKANA[char]) {
      let fullChar = HALF_TO_FULL_KATAKANA[char];

      // 次が濁点(ﾞ)の場合
      if (nextChar === 'ﾞ' && DAKUTEN_MAP[fullChar]) {
        fullChar = DAKUTEN_MAP[fullChar];
        i++;
      }
      // 次が半濁点(ﾟ)の場合
      else if (nextChar === 'ﾟ' && HANDAKUTEN_MAP[fullChar]) {
        fullChar = HANDAKUTEN_MAP[fullChar];
        i++;
      }

      result += fullChar;
    } else {
      result += char;
    }

    i++;
  }

  return result;
}

/**
 * 全角カタカナを全角ひらがなに変換
 * カタカナ(U+30A1-U+30F6) → ひらがな(U+3041-U+3096): -0x60
 */
export function katakanaToHiragana(str: string): string {
  return str.replace(/[\u30A1-\u30F6]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0x60));
}

/**
 * カナ文字列を正規化して全角カタカナ・全角ひらがなを取得
 */
export function normalizeKana(str: string): { katakana: string; hiragana: string } {
  const katakana = halfToFullKatakana(str);
  const hiragana = katakanaToHiragana(katakana);
  return { katakana, hiragana };
}
